import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { WORKSPACE_TAB_LABELS, type WorkspaceTab } from './workspaceTabs';

interface Props {
  tabs: WorkspaceTab[];
  active: WorkspaceTab;
  isLocked: (tab: WorkspaceTab) => boolean;
  onSelect: (tab: WorkspaceTab) => void;
  /** `b`: Direction B's segmented control in the middle of the top bar (beta only). `b-phone`: on a phone, one control
   * naming the open view, whose menu lists the views. */
  variant?: 'b' | 'b-phone';
}

export function WorkspaceTabStrip(props: Props) {
  if (props.variant === 'b-phone') return <PhoneViewMenu {...props} />;
  return <TabStrip {...props} />;
}

/** Direction B (beta only): holds the view switch's place, at its height, while the studio decides which view opens. */
export function WorkspaceTabStripSkeleton({ variant }: { variant: 'b' | 'b-phone' }) {
  return (
    <div
      aria-hidden="true"
      data-view-skeleton
      className={cn(
        'h-8 animate-pulse bg-b-raised motion-reduce:animate-none',
        variant === 'b' ? 'w-56 rounded-[9px] shadow-[inset_0_0_0_1px_hsl(var(--b-line))]' : 'w-28 rounded-[var(--b-radius-control)]',
      )}
    />
  );
}

function TabStrip({ tabs, active, isLocked, onSelect, variant }: Props) {
  const b = variant === 'b';
  return (
    <div
      role="tablist"
      aria-label="Workspace views"
      className={b ? 'flex items-center gap-0.5 rounded-[9px] bg-b-raised p-[3px] shadow-[inset_0_0_0_1px_hsl(var(--b-line))]' : 'flex items-center gap-0.5 rounded-lg bg-muted p-0.5'}
    >
      {tabs.map((tab) => {
        const locked = isLocked(tab);
        const selected = active === tab;
        return (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => !locked && onSelect(tab)}
            disabled={locked}
            title={tab === 'blog' ? (locked ? 'Finish the video first' : 'Blog companion') : undefined}
            className={cn(
              b
                ? 'h-[26px] whitespace-nowrap rounded-[7px] px-3.5 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                : 'rounded-md px-3.5 py-1.5 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              b
                ? selected ? 'bg-b-elevated text-b-t1 shadow-[var(--b-shadow-lift)]' : 'text-b-t3 hover:text-b-t1'
                : selected ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              locked && 'cursor-not-allowed opacity-40',
            )}
          >
            {WORKSPACE_TAB_LABELS[tab]}
          </button>
        );
      })}
    </div>
  );
}

function PhoneViewMenu({ tabs, active, isLocked, onSelect }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e: Event) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex h-8 items-center gap-1 rounded-[var(--b-radius-control)] px-2.5 text-b-control text-b-t1 hover:bg-b-raised"
      >
        {WORKSPACE_TAB_LABELS[active]}
        <ChevronDown className="h-4 w-4 text-b-t3" />
      </button>
      {open && (
        <div role="menu" aria-label="Workspace views" className="absolute left-1/2 top-full z-50 mt-1 w-44 -translate-x-1/2 rounded-[var(--b-radius-card)] border border-b-line bg-b-elevated p-1 shadow-[var(--b-shadow-lift)]">
          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              role="menuitemradio"
              aria-checked={tab === active}
              disabled={isLocked(tab)}
              onClick={() => { setOpen(false); if (tab !== active) onSelect(tab); }}
              className={cn(
                'flex h-10 w-full items-center rounded-[var(--b-radius-control)] px-3 text-left text-b-control disabled:opacity-40',
                tab === active ? 'bg-b-raised text-b-t1' : 'text-b-t2 hover:bg-b-raised',
              )}
            >
              {WORKSPACE_TAB_LABELS[tab]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
